import type { NextPage } from 'next'
import { useState } from 'react'

import Footer from '@/components/footer'
import Header from '@/components/navigation'
import Picture from '@/components/picture'
import TabHeaderButton from '@/components/tabHeaderButton'
import ValueDetailedItem from '@/components/values/valueDetailedItem'
import ValueSummaryItem from '@/components/values/valueSummaryItem'
import { STRINGS, values } from '@/constants/company'
import styles from '@/styles/CompanyPage.module.css'

export async function getStaticProps() {
  return {
    props: {
      title: 'About DeHaat',
      description:
        'The fastest-growing AI-enabled start-up in Agricultural Technology sector. Founded by the alumni from IIT Delhi, IIT Kharagpur, IIM Ahmedabad and other top institutes.',
    },
  }
}

const Company: NextPage = () => {
  const [activeTab, setActiveTab] = useState(0)

  return (
    <>
      <div className="p-4 px-8">
        <main className="max-w-7xl m-auto font-natosans">
          <Header />
          <section className="mt-4 h-full w-full">
            <h1 className="md:text-5xl text-3xl leading-snug pb-2 md:px-4">
              {STRINGS.HEADING}
            </h1>
            <header className="w-full flex md:items-start items-center flex-wrap">
              <article className="md:w-3/5 md:px-4 md:order-1 order-2">
                <p className="md:text-lg md:leading-relaxed whitespace-pre-wrap pt-6 text-[15px] text-neutral-80 leading-normal font-natosans-light">
                  {STRINGS.ABOUT_DEHAAT_DETAILED.split(/<@>(.+?)<\/@>/g).map(
                    (text, index) =>
                      index % 2 == 0 ? (
                        text
                      ) : (
                        <span className="font-natosans-medium" key={text}>
                          {text}
                        </span>
                      )
                  )}
                </p>
              </article>
              <aside className="md:w-2/5 block w-full md:px-4 pt-6 md:order-2 order-1">
                <Picture
                  image="/assets/images/about-us/founders"
                  imageAlt={STRINGS.DEHAAT_FOUNDERS}
                  imageStyle="h-full w-full"
                />
              </aside>
            </header>
          </section>
          <section className="mt-12 md:px-4">
            <h2 className="text-neutral-100 md:text-[44px] text-[32px] leading-[1.1875] md:mb-6 mb-4">
              {STRINGS.OUR_VALUES}
            </h2>
            <p className="md:text-[22px] leading-[1.875] text-base text-neutral-90 md:mb-16 mb-8 font-natosans-light">
              {STRINGS.OUR_VALUES_SUBHEADING}
            </p>
            <section className="md:flex md:space-x-4 md:space-y-0 space-y-6">
              {values.map(({ title, Icon, values }) => (
                <ValueSummaryItem
                  key={`summary-${title}`}
                  title={title}
                  Icon={Icon}
                  values={values}
                  listStyle={styles.dashedList}
                />
              ))}
            </section>
          </section>
          <section className="mt-[110px] hidden md:block mb-4">
            <ul className="h-16 border-neutral-30 rounded-xl border-[1.5px] flex overflow-hidden mb-16 mx-auto max-w-[921px]">
              {values.map(({ title, id }, index) => (
                <li key={`tab-button-${title}`} className="relative flex-1">
                  <TabHeaderButton
                    label={title}
                    onClick={() => setActiveTab(index)}
                    isActive={index === activeTab}
                    controls={`content-${id}`}
                    btnId={`desktop-tab-${id}`}
                    showSeperator={
                      index + 1 < values.length && index !== activeTab
                    }
                  />
                </li>
              ))}
            </ul>

            {values.map(({ image, id, title, description }, index) => (
              <ValueDetailedItem
                id={`content-${id}`}
                key={`content-${id}`}
                image={image}
                imageAlt={title}
                descriptionList={description}
                isActive={activeTab === index}
                sectionStyle={styles.fadeIn}
                listStyle={styles.dashedList}
              />
            ))}
          </section>
          <section className="md:hidden space-y-6 mt-10 mb-4">
            {values.map(({ title, id, image, description }, index) => (
              <div key={`mobile-accordion-${id}`}>
                <button
                  className={`px-5 py-[18px] relative w-full rounded-xl ${
                    activeTab === index
                      ? 'bg-primary-100 text-white'
                      : 'bg-neutral-20 text-neutral-100'
                  } transition-colors duration-300 text-left`}
                  onClick={() => setActiveTab(index)}
                  aria-expanded={activeTab === index}
                  id={`mobile-accordion-${id}`}
                  aria-controls={`mobile-content-${id}`}
                >
                  {title}
                  <span
                    className={`${
                      activeTab === index ? 'bg-white' : 'bg-neutral-100'
                    } h-[1.5px] w-3 absolute right-5 top-1/2 -translate-y-1/2`}
                  />
                  <span
                    className={`${
                      activeTab === index ? 'opacity-0' : 'opacity-100'
                    } absolute w-[1.5px] h-3 right-[25.5px] bg-neutral-100 top-1/2 -translate-y-1/2`}
                  />
                </button>
                <ValueDetailedItem
                  id={`mobile-content-${id}`}
                  key={`content-${id}`}
                  image={image}
                  imageAlt={title}
                  descriptionList={description}
                  isActive={activeTab === index}
                  sectionStyle={`${styles.fadeIn} mt-6`}
                  listStyle={styles.dashedList}
                  aria-labelledby={`mobile-accordion-${id}`}
                />
              </div>
            ))}
          </section>
        </main>
      </div>
      <Footer />
    </>
  )
}

export default Company
